import React, { useContext, useState } from 'react';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ShopType } from '../../../../types/shop';
import { PROMOTION } from '../../../../constants/define/translation';
import { btnActive } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { deleteTileWidgetRepository } from '../../../../repository/mytm/shop/shopRepository';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { CONFIG_CONTAINER_NAME, deleteBlob } from '../../../../utils/azure';

interface ActionsColumnProps {
  record: ShopType;
  setIsOpenModal: Function;
  setIsOpenScheduleModal: Function;
  setDataRecord: Function;
  onRefresh: () => void;
  setLoading?: (value: boolean) => void;
  handleGetScheduleWidgetByID: Function;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(PROMOTION);
  const {
    record,
    setIsOpenModal,
    setDataRecord,
    onRefresh,
    setLoading,
    handleGetScheduleWidgetByID,
    setIsOpenScheduleModal,
  } = props;
  const [deleting, setDeleting] = useState(false);
  const userContext = useContext(UserContext);

  const handleDelete = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          deleteTileWidgetRepository(record?.id)
            .then(res => {
              deleteBlob(record?.imageLink || record?.videoLink, CONFIG_CONTAINER_NAME.UPLOADS)
                .then()
                .catch();
              message.success(t('message.success.deletePromotion'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              onRefresh();
              setDeleting(false);
            });
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeleting(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleSchedule = async () => {
    await handleGetScheduleWidgetByID(record.id);
    setIsOpenScheduleModal(true);
  };

  const handleEdit = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.edit')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              setDataRecord(record);
              setIsOpenModal(true);
              setLoading && setLoading(false);
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
              setLoading && setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.tooltip.edit')}>
          <Button icon={<EditOutlined />} onClick={handleEdit} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.schedule')}>
          <Button icon={<CalendarOutlined />} onClick={handleSchedule} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.delete')}>
          <Button
            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
            onClick={handleDelete}
            disabled={deleting}
            loading={deleting}
          />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}
`;
